/**
  * @file
  *
- * this file contains the component which controls the inactivity dialog.
+ * This component manages user session inactivity across the application.
+ * It displays a warning dialog after 25 minutes of inactivity, giving users
+ * 5 minutes to continue their session or be logged out automatically.
+ * The component listens for user activity via custom events dispatched by
+ * service functions throughout the application.
  */

import React, { useCallback, useRef, useState, useEffect } from 'react';
import { Stack, Typography } from '@mui/material';
import DialogWrapper from './DialogWrapper';
import {
  getServiceInstance,
  redirectToLoginPage,
  getSessionInfoCookieName,
  resetInactivityTimerAndUpdateCookie,
  deleteAllCookies,
  getParsedCookie,
} from 'service';
import { purgeIDB } from 'reportView/worker';
import { formatCountdownSeconds } from 'date';
import { useCurrentTabId } from './TabIndexedStorageContext';
import { useDisclosure } from '../hooks/useDisclosure';
import { getRefreshTokenFromCookie } from 'utils';

// Constants for timeouts
const INACTIVITY_TIMEOUT_MS = 25 * 60 * 1000; // 25 minutes
const WARNING_COUNTDOWN_SECONDS = 300; // 5 minutes

export default function GlobalInactivityDialog() {
  const tabId = useCurrentTabId();

  const {
    isOpen: isInactivityDialogOpen,
    open: openInactivityDialog,
    close: closeInactivityDialog,
  } = useDisclosure(false);

  const inactivityTimeoutRef = useRef(null);
  const lastAppUsedTimeRef = useRef(null);

  const clearInactivityTimeout = useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }
  }, []);

  const handleLogout = useCallback(async () => {
    closeInactivityDialog();
    clearInactivityTimeout();
    try {
      await purgeIDB({ tabId });

      const refreshToken = getRefreshTokenFromCookie();

      await getServiceInstance(undefined, { refreshToken }).post('/logout', {
        withCredentials: true,
      });

      redirectToLoginPage(window.location.origin);
    } catch (error) {
      console.error('Error during logout:', error);
      redirectToLoginPage(window.location.origin);
    } finally {
      deleteAllCookies();
    }
  }, [closeInactivityDialog, clearInactivityTimeout, tabId]);

  // Inactivity timer logic: when timer expires, open dialog and set initial countdown
  const [initialCountdown, setInitialCountdown] = useState(null);

  /* setupInactivityTimer sets up a timeout to detect user inactivity.
   It reads the last time the app was used from a session cookie.
   If the user has been inactive for longer than the allowed timeout,
   it opens the inactivity warning dialog and starts a countdown to logout.
   The timer is reset whenever user activity is detected or the inactivity timer is manually reset. */
  const setupInactivityTimer = useCallback(() => {
    clearInactivityTimeout();
    let lastAppUsedTime = null;
    const cookieName = getSessionInfoCookieName();
    const parsed = getParsedCookie(cookieName);
    if (parsed?.lastAppUsedTime) {
      lastAppUsedTime = Number(parsed.lastAppUsedTime);
    }
    lastAppUsedTimeRef.current = lastAppUsedTime;
    const now = Date.now();
    const lastUsed = lastAppUsedTime || now;
    const msSinceLastUsed = now - lastUsed;
    const msUntilInactivity = Math.max(0, INACTIVITY_TIMEOUT_MS - msSinceLastUsed);
    inactivityTimeoutRef.current = setTimeout(() => {
      openInactivityDialog();
      setInitialCountdown(WARNING_COUNTDOWN_SECONDS);
    }, msUntilInactivity);
  }, [clearInactivityTimeout, openInactivityDialog]);

  // Handler for continuing session
  const handleContinue = useCallback(() => {
    resetInactivityTimerAndUpdateCookie();
    closeInactivityDialog();
    setInitialCountdown(null);
    setupInactivityTimer();
  }, [closeInactivityDialog, setupInactivityTimer]);

  // On mount, set up inactivity timer and listen for reset events
  useEffect(() => {
    setupInactivityTimer();
    const resetListener = () => setupInactivityTimer();
    window.addEventListener('reset-inactivity-timer', resetListener);
    const pollInterval = setInterval(() => {
      const cookieName = getSessionInfoCookieName();
      const parsed = getParsedCookie(cookieName);
      const newLastAppUsedTime = parsed?.lastAppUsedTime ? Number(parsed.lastAppUsedTime) : null;
      if (
        newLastAppUsedTime &&
        lastAppUsedTimeRef.current &&
        newLastAppUsedTime > lastAppUsedTimeRef.current
      ) {
        setupInactivityTimer();
        if (isInactivityDialogOpen) {
          closeInactivityDialog();
        }
      }
    }, 2000);
    return () => {
      clearInactivityTimeout();
      window.removeEventListener('reset-inactivity-timer', resetListener);
      setInitialCountdown(null);
      clearInterval(pollInterval);
    };
  }, [setupInactivityTimer, clearInactivityTimeout, isInactivityDialogOpen, closeInactivityDialog]);

  return (
    <>
      {isInactivityDialogOpen && (
        <DialogWrapper
          disableBackdropClick
          isOpen={isInactivityDialogOpen}
          closeDialog={closeInactivityDialog}
          title="Session Inactive"
          secondaryBtnText="Logout"
          secondaryBtnAction={handleLogout}
          primaryBtnText="Continue working"
          primaryBtnAction={handleContinue}
        >
          <Stack>
            <Typography>You have been inactive for a while.</Typography>
            {typeof initialCountdown === 'number' && initialCountdown > 0 && (
              <CountdownDisplay initialCountdown={initialCountdown} handleLogout={handleLogout} />
            )}
          </Stack>
        </DialogWrapper>
      )}
    </>
  );
}

// Memoized countdown display with local state and timer
const CountdownDisplay = React.memo(function CountdownDisplay({ initialCountdown, handleLogout }) {
  const [countdown, setCountdown] = React.useState(initialCountdown);

  useEffect(() => {
    if (typeof countdown !== 'number') return;
    if (countdown <= 0) {
      handleLogout();
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, handleLogout]);

  if (typeof countdown !== 'number' || countdown <= 0) return null;

  return (
    <Typography>
      Your session will expire in <strong>{formatCountdownSeconds(countdown)}</strong> unless you
      continue.
    </Typography>
  );
});
